"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserManagement = void 0;
var tslib_1 = require("tslib");
var utils_1 = require("../../utils/utils");
var authenticationManager_1 = require("./authentication/firebase/authenticationManager");
var authorizationManager_1 = require("./authorization/velvet/authorizationManager");
var velvetAuthorizationManager_1 = require("./authorization/velvet/velvetAuthorizationManager");
var constants_1 = require("../../constants/constants");
var networkServiceDefault_1 = require("../network/networkServiceDefault");
var tokenManager_1 = require("./token/velvet/tokenManager");
/**
 * Implementation of the UserManagementProvider interface.
 * @class
 * @implements {UserManagementProvider}
 * @remarks This class provides the implementation for user management operations.
 */
var UserManagement = /** @class */ (function () {
    function UserManagement(networkService, tokenManager) {
        if (networkService === void 0) { networkService = new networkServiceDefault_1.NetworkServiceDefault(); }
        if (tokenManager === void 0) { tokenManager = new tokenManager_1.TokenManager(); }
        this.authenticationManager = new authenticationManager_1.AuthenticationManager();
        this.authorizationManager = new authorizationManager_1.AuthorizationManager(networkService, tokenManager);
        this.tokenManager = tokenManager;
    }
    UserManagement.prototype.configure = function () {
        try {
            this.authenticationManager.initialize();
        }
        catch (error) {
            console.error('Error during configuration process:', error);
            throw error;
        }
    };
    UserManagement.prototype.register = function (email, password, subscription) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var authUser, userID, authorizationResponse, user, error_1;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 3, , 6]);
                        return [4 /*yield*/, this.authenticationManager.register(email, password)];
                    case 1:
                        authUser = _c.sent();
                        userID = authUser.userID;
                        if (!userID) {
                            throw new velvetAuthorizationManager_1.RegistrationError('User ID not found during registration.');
                        }
                        return [4 /*yield*/, this.authorizationManager.register(userID, subscription)];
                    case 2:
                        authorizationResponse = _c.sent();
                        user = {
                            userID: authUser.userID,
                            email: authUser.email,
                            name: authUser.name,
                            photoURL: authUser.photoURL,
                            confirmed: (_a = authorizationResponse === null || authorizationResponse === void 0 ? void 0 : authorizationResponse.user) === null || _a === void 0 ? void 0 : _a.confirmed,
                            subscription: (_b = authorizationResponse === null || authorizationResponse === void 0 ? void 0 : authorizationResponse.user) === null || _b === void 0 ? void 0 : _b.subscription,
                            // TODO: Verify during web implementation whether the token should be stored using this approach
                            // token: authorizationResponse?.token
                        };
                        (0, utils_1.saveObjectToLocalStorage)(constants_1.userDataLS, user);
                        return [2 /*return*/, user];
                    case 3:
                        error_1 = _c.sent();
                        if (!(error_1 instanceof velvetAuthorizationManager_1.RegistrationError)) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.authenticationManager.deleteUser()]; // Rollback user creation
                    case 4:
                        _c.sent(); // Rollback user creation
                        _c.label = 5;
                    case 5:
                        console.error('Registration failed:', error_1);
                        throw error_1;
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.verifyCode = function (code) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var confirmVerificationCodeResponse, error_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authorizationManager.confirmVerificationCode(code)];
                    case 1:
                        confirmVerificationCodeResponse = _a.sent();
                        if (!confirmVerificationCodeResponse) {
                            throw new Error('Verification code confirmation failed: no response received.');
                        }
                        return [2 /*return*/, confirmVerificationCodeResponse];
                    case 2:
                        error_2 = _a.sent();
                        console.error('Confirmation of the verification code failed:', error_2);
                        throw new Error("Verification code confirmation failed: ".concat(error_2.message || error_2));
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.resendCode = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var resendVerificationCodeResponse, error_3;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authorizationManager.resendVerificationCode()];
                    case 1:
                        resendVerificationCodeResponse = _a.sent();
                        return [2 /*return*/, !!resendVerificationCodeResponse]; // Convert truthy/falsy value to boolean
                    case 2:
                        error_3 = _a.sent();
                        console.error('Resending the verification code failed:', error_3);
                        throw new Error("Resend verification code failed: ".concat(error_3.message || error_3));
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.login = function (email, password) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var authUser, userID, authorizationResponse, user, velvetUser, error_4;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 3, , 6]);
                        return [4 /*yield*/, this.authenticationManager.login(email, password)];
                    case 1:
                        authUser = _c.sent();
                        userID = authUser.userID;
                        return [4 /*yield*/, this.authorizationManager.login(userID)];
                    case 2:
                        authorizationResponse = _c.sent();
                        user = {
                            userID: authUser.userID,
                            email: authUser.email,
                            name: authUser.name,
                            photoURL: authUser.photoURL,
                            confirmed: (_a = authorizationResponse === null || authorizationResponse === void 0 ? void 0 : authorizationResponse.user) === null || _a === void 0 ? void 0 : _a.confirmed,
                            subscription: (_b = authorizationResponse === null || authorizationResponse === void 0 ? void 0 : authorizationResponse.user) === null || _b === void 0 ? void 0 : _b.subscription,
                        };
                        (0, utils_1.saveObjectToLocalStorage)(constants_1.userDataLS, user);
                        velvetUser = tslib_1.__assign(tslib_1.__assign({}, user), { token: authorizationResponse === null || authorizationResponse === void 0 ? void 0 : authorizationResponse.token, refreshToken: authorizationResponse === null || authorizationResponse === void 0 ? void 0 : authorizationResponse.refreshToken });
                        return [2 /*return*/, velvetUser];
                    case 3:
                        error_4 = _c.sent();
                        if (!(error_4 instanceof velvetAuthorizationManager_1.LoginError)) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.authenticationManager.logout()];
                    case 4:
                        _c.sent();
                        _c.label = 5;
                    case 5:
                        console.error('Login failed:', error_4);
                        throw error_4;
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.logout = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var error_5;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authenticationManager.logout()];
                    case 1:
                        _a.sent();
                        (0, utils_1.removeItemFromLocalStorage)(constants_1.userDataLS);
                        (0, utils_1.removeItemFromLocalStorage)(constants_1.userTokensLS);
                        (0, utils_1.removeItemFromLocalStorage)(constants_1.authAccessTokenLS);
                        return [3 /*break*/, 3];
                    case 2:
                        error_5 = _a.sent();
                        console.error('Logout failed:', error_5);
                        throw error_5;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.getUserDetails = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var user;
            return tslib_1.__generator(this, function (_a) {
                try {
                    user = (0, utils_1.getLocalStorageItemByKey)(constants_1.userDataLS);
                    return [2 /*return*/, user];
                }
                catch (error) {
                    console.error('Error getting user details', error);
                    throw error;
                }
                return [2 /*return*/];
            });
        });
    };
    UserManagement.prototype.updateUserDetails = function (userDetails) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var user, userStored, error_6;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authenticationManager.updateUserDetails(userDetails)];
                    case 1:
                        user = _a.sent();
                        if (user) {
                            userStored = (0, utils_1.getLocalStorageItemByKey)(constants_1.userDataLS);
                            if (userStored) {
                                userStored.name = user === null || user === void 0 ? void 0 : user.name;
                                userStored.photoURL = user === null || user === void 0 ? void 0 : user.photoURL;
                                (0, utils_1.saveObjectToLocalStorage)(constants_1.userDataLS, userStored);
                            }
                        }
                        return [2 /*return*/, user];
                    case 2:
                        error_6 = _a.sent();
                        console.error('Error updating user details', error_6);
                        throw error_6;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.loginPinPair = function (token) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var authResponse, accessToken, accessTokenProp, _c, email, uid, displayName, photoURL, response, updatedUser, activeTokens, error_7;
            return tslib_1.__generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        _d.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, this.authenticationManager.signInWithCustomToken(token)];
                    case 1:
                        authResponse = _d.sent();
                        accessToken = '';
                        accessTokenProp = 'accessToken';
                        if (Object.prototype.hasOwnProperty.call(authResponse.user, accessTokenProp)) {
                            accessToken = authResponse.user[accessTokenProp];
                        }
                        _c = authResponse.user, email = _c.email, uid = _c.uid, displayName = _c.displayName, photoURL = _c.photoURL;
                        return [4 /*yield*/, this.authorizationManager.login(uid)];
                    case 2:
                        response = _d.sent();
                        updatedUser = {
                            userID: uid,
                            email: email,
                            name: displayName,
                            photoURL: photoURL,
                            confirmed: (_a = response === null || response === void 0 ? void 0 : response.user) === null || _a === void 0 ? void 0 : _a.confirmed,
                            subscription: (_b = response === null || response === void 0 ? void 0 : response.user) === null || _b === void 0 ? void 0 : _b.subscription,
                        };
                        activeTokens = {
                            accessToken: response === null || response === void 0 ? void 0 : response.token,
                            accessRefreshToken: response === null || response === void 0 ? void 0 : response.refreshToken,
                        };
                        (0, utils_1.saveObjectToLocalStorage)(constants_1.userDataLS, updatedUser);
                        (0, utils_1.saveObjectToLocalStorage)(constants_1.userTokensLS, activeTokens);
                        return [3 /*break*/, 4];
                    case 3:
                        error_7 = _d.sent();
                        throw error_7;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.isEmailValid = function (email) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var emailCheck, error_8;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authenticationManager.fetchSignInMethodsForEmail(email)];
                    case 1:
                        emailCheck = _a.sent();
                        return [2 /*return*/, emailCheck];
                    case 2:
                        error_8 = _a.sent();
                        console.error('Error while checking user email', error_8);
                        throw error_8;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.resetPassword = function (email) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var error_9;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authenticationManager.resetPassword(email)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_9 = _a.sent();
                        console.error('Error while sending user reset password email', error_9);
                        throw error_9;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.getUser = function () {
        var _a, _b, _c;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var authorizationUser, authenticationUser, velvetUser, error_10;
            return tslib_1.__generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        _d.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authorizationManager.getUser()];
                    case 1:
                        authorizationUser = _d.sent();
                        authenticationUser = this.authenticationManager.getUser();
                        if (authorizationUser && authenticationUser) {
                            velvetUser = {
                                userID: (_a = authorizationUser.user) === null || _a === void 0 ? void 0 : _a.firebaseUid,
                                subscription: (_b = authorizationUser.user) === null || _b === void 0 ? void 0 : _b.subscription,
                                confirmed: (_c = authorizationUser.user) === null || _c === void 0 ? void 0 : _c.confirmed,
                                email: authenticationUser === null || authenticationUser === void 0 ? void 0 : authenticationUser.email,
                                name: authenticationUser.displayName,
                                photoURL: authenticationUser.photoURL,
                            };
                            return [2 /*return*/, velvetUser];
                        }
                        else {
                            return [2 /*return*/, null];
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        error_10 = _d.sent();
                        console.error('Error during check user process:', error_10);
                        throw error_10;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.isUserLoggedIn = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var tokens;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.tokenManager.refreshTokens()];
                    case 1:
                        tokens = _a.sent();
                        return [2 /*return*/, !!tokens];
                }
            });
        });
    };
    UserManagement.prototype.fetchPinPairCode = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var error_11;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authorizationManager.fetchPinPairCode()];
                    case 1: return [2 /*return*/, _a.sent()];
                    case 2:
                        error_11 = _a.sent();
                        console.error('Error in AuthorizationManager getLoginCode:', error_11);
                        return [2 /*return*/, null];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.fetchPinPairToken = function (code) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var error_12;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authorizationManager.fetchPinPairToken(code)];
                    case 1: return [2 /*return*/, _a.sent()];
                    case 2:
                        error_12 = _a.sent();
                        console.error('Error in AuthorizationManager getCustomTokenWithCode:', error_12);
                        return [2 /*return*/, null];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    UserManagement.prototype.getAccessToken = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var error_13;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.authenticationManager.getAccessToken()];
                    case 1: return [2 /*return*/, _a.sent()];
                    case 2:
                        error_13 = _a.sent();
                        console.error('Error in AuthorizationManager getAccessToken:', error_13);
                        return [2 /*return*/, null];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    return UserManagement;
}());
exports.UserManagement = UserManagement;
